import { useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import {
  AlertTriangle,
  Building2,
  CalendarDays,
  CheckCircle2,
  CircleDashed,
  Clock3,
  Database,
  FileText,
  Handshake,
  LoaderCircle,
  Mail,
  Radar,
  Search,
  ShieldAlert,
  UserRound,
  type LucideIcon,
} from 'lucide-react';
import type { ActivityRecommendedActionKind } from '@shared/types/activity-feed';
import type { ThreadVisibleState } from '@shared/types/thread-work';

export const TopicStatusIcon = ({ state }: { state: ThreadVisibleState }) => {
  if (state === 'running') return <LoaderCircle className="h-3.5 w-3.5 animate-spin" />;
  if (state === 'awaiting_review') return <AlertTriangle className="h-3.5 w-3.5" />;
  if (state === 'waiting') return <Clock3 className="h-3.5 w-3.5" />;
  if (state === 'monitoring') return <Radar className="h-3.5 w-3.5" />;
  if (state === 'needs_preparation') return <CircleDashed className="h-3.5 w-3.5" />;
  if (state === 'blocked') return <ShieldAlert className="h-3.5 w-3.5" />;
  return <CheckCircle2 className="h-3.5 w-3.5" />;
};

const getTaskVisualIcon = (
  actionKind: ActivityRecommendedActionKind | null,
  subjectKind: string,
  taskTitle: string,
): LucideIcon => {
  switch (actionKind) {
    case 'email':
    case 'follow_up':
    case 'personalized_email':
      return Mail;
    case 'meeting_prep':
      return CalendarDays;
    case 'research':
      return Search;
    case 'crm_update':
      return Database;
    case 'diagnostic':
    case 'run_review':
      return FileText;
    default:
      break;
  }

  const normalizedTitle = taskTitle.toLowerCase();
  if (/email|brouillon|relance|suivi|envoi/.test(normalizedTitle)) return Mail;
  if (/recherch|décideur|qualifi/.test(normalizedTitle)) return Search;
  if (/réunion|appel|meeting/.test(normalizedTitle)) return CalendarDays;
  if (/crm|fiche/.test(normalizedTitle)) return Database;
  if (/synthèse|brief|argumentaire|dpa|analyse/.test(normalizedTitle)) return FileText;

  switch (subjectKind) {
    case 'contact':
      return UserRound;
    case 'deal':
      return Handshake;
    case 'meeting':
    case 'call':
      return CalendarDays;
    case 'email':
      return Mail;
    case 'document':
      return FileText;
    default:
      return Building2;
  }
};

export const AccountTaskVisual = ({
  imageUrl,
  actionKind,
  subjectKind,
  taskTitle,
}: {
  imageUrl: string | null | undefined;
  actionKind: ActivityRecommendedActionKind | null;
  subjectKind: string;
  taskTitle: string;
}) => {
  const [imageFailed, setImageFailed] = useState(false);
  const Icon = getTaskVisualIcon(actionKind, subjectKind, taskTitle);

  return (
    <div
      data-testid="activity-task-visual"
      className="flex h-8 w-8 shrink-0 items-center justify-center overflow-hidden rounded-lg border border-domino-cardBorder bg-domino-lilac/60 text-domino-aubergine"
    >
      {imageUrl && !imageFailed ? (
        <img
          src={imageUrl}
          alt=""
          referrerPolicy="no-referrer"
          className="h-full w-full bg-domino-white p-1 object-contain"
          onError={() => setImageFailed(true)}
        />
      ) : (
        <Icon aria-hidden="true" className="h-4 w-4" />
      )}
    </div>
  );
};

/**
 * The first cell of a row in Mon travail when the work has no account: what
 * it belongs to (an app, a ritual) and one short detail. Same place and size
 * as the account cell, so every row reads the same way.
 */
export const RowIdentity = ({ icon: Icon, label, href, detail }: {
  icon: LucideIcon;
  label: string;
  href?: string;
  detail?: ReactNode;
}) => (
  <div className="flex min-w-0 items-center gap-2.5">
    <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border border-domino-cardBorder bg-domino-lilac/60 text-domino-aubergine">
      <Icon aria-hidden="true" className="h-4 w-4" />
    </div>
    <div className="min-w-0">
      {href
        ? <Link to={href} title={label} className="block truncate text-sm font-semibold leading-5 text-domino-aubergine hover:underline focus-visible:ring-2 focus-visible:ring-domino-prune">{label}</Link>
        : <p title={label} className="truncate text-sm font-semibold leading-5 text-domino-aubergine">{label}</p>}
      {detail && <div className="flex items-center gap-1 truncate text-xs leading-4 text-domino-inkSoft">{detail}</div>}
    </div>
  </div>
);
